'use client';

import { ActionIcon, Indicator, Tooltip } from '@mantine/core';
import { IconHistory } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useUnreadPatchLogCount } from '@hooks/patch-logs/use-patch-logs';
import { pageUrl } from '@constants/url';
import { useLanguage } from '@contexts/LanguageContext';
import { useTheme } from '@contexts/ThemeContext';
import { resolvePatchLogCopy } from './i18n';
import { isPatchLogLocale, type PatchLogLocale } from './types';

/** Matches the notification bell it sits next to. */
const ICON_SIZE = 20;

/**
 * Header shortcut to Riwayat Pembaruan.
 *
 * It navigates to the history page rather than opening the changelog in a
 * modal: the same content in two different shells is two things to keep in
 * step, and the page is the one the ticket specifies (it carries the
 * full-screen image viewer that a modal cannot).
 *
 * It lives in the header because three surfaces hide the sidebar entirely —
 * the PIB editor, the review screen and export — so the sidebar footer entry
 * point is unreachable from them.
 *
 * The dot is the unread badge (Issue 13). It counts logs the user has not
 * confirmed at the current revision, including the ones they deferred past the
 * cap: dismissing the modal three times silences the interruption, not the
 * fact that there is something unread.
 */
export const PatchLogHistoryButton = () => {
  const { colors: themeColors } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const locale: PatchLogLocale = isPatchLogLocale(language) ? language : 'id';
  const label = resolvePatchLogCopy(locale, 'history');
  const unread = useUnreadPatchLogCount();
  const ariaLabel = unread > 0 ? `${label} (${unread})` : label;

  return (
    <Tooltip label={ariaLabel} withArrow>
      <Indicator
        // `disabled` rather than a conditional wrapper: the icon must not
        // shift by a pixel when the badge appears or clears.
        disabled={unread === 0}
        label={unread > 9 ? '9+' : unread}
        size={16}
        offset={4}
        color="red"
        aria-hidden
      >
        <ActionIcon
          variant="subtle"
          size="lg"
          radius="xl"
          aria-label={ariaLabel}
          onClick={() => navigate(pageUrl.riwayatPembaruan)}
          style={{ color: themeColors.text }}
        >
          <IconHistory size={ICON_SIZE} strokeWidth={1.6} />
        </ActionIcon>
      </Indicator>
    </Tooltip>
  );
};

export default PatchLogHistoryButton;
